import React, { useEffect, useMemo, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import classes from "./QuoteModal.module.css";
import { PackageId, formatLongDate } from "./pricing";
import {
  BOOKING_EMAIL,
  buildBookingEmailText,
  buildBookingMailto,
  buildQuote,
  checkOutDateFor,
  clampEventDate,
  formatMoney,
  nightsInRange,
} from "./quote";

const QuoteModal = ({
  pkg,
  packageName,
  eventDate,
  today,
  isDateSelectable,
  isBooked,
  onClose,
}: {
  pkg: PackageId;
  packageName: string;
  eventDate: Date;
  today: Date;
  isDateSelectable: (date: Date) => boolean;
  isBooked: (date: Date) => boolean;
  onClose: () => void;
}) => {
  // Opens on the event date chosen on the page, as a single day.
  const [rangeStart, setRangeStart] = useState<Date>(eventDate);
  const [rangeEnd, setRangeEnd] = useState<Date | null>(null);
  const [copied, setCopied] = useState(false);
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const effectiveEnd = rangeEnd ?? rangeStart;
  // Derived from the selection rather than the quote, so the hint stays hidden
  // even when the chosen range fails to price (closed dates, past dates).
  const selectedDays = nightsInRange(rangeStart, effectiveEnd).length;
  const stayEventDate = useMemo(
    () => clampEventDate(eventDate, rangeStart, effectiveEnd),
    [eventDate, rangeStart, effectiveEnd],
  );

  const result = useMemo(
    () =>
      buildQuote(
        pkg,
        packageName,
        stayEventDate,
        rangeStart,
        effectiveEnd,
        today,
        isBooked,
      ),
    [pkg, packageName, stayEventDate, rangeStart, effectiveEnd, today, isBooked],
  );

  const quote = result.status === "ok" ? result.quote : null;
  const emailText = quote
    ? buildBookingEmailText(
        packageName,
        stayEventDate,
        rangeStart,
        effectiveEnd,
        quote,
      )
    : "";

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(emailText);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2500);
    } catch {
      setCopied(false);
    }
  };

  const nightCount = quote?.lines.length ?? 0;

  return (
    <div
      className={classes.backdrop}
      onClick={onClose}
      role="presentation"
    >
      <div
        className={classes.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="quote-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className={classes.head}>
          <h2 id="quote-title" className={classes.title}>
            {packageName} Package — your quote
          </h2>
          <button
            ref={closeRef}
            type="button"
            className={classes.close}
            onClick={onClose}
            aria-label="Close quote"
          >
            ×
          </button>
        </div>

        <div className={classes.body}>
          <div className={classes.stayPicker}>
            <span className={classes.label}>Your stay</span>
            <DatePicker
              selectsRange
              startDate={rangeStart}
              endDate={rangeEnd}
              minDate={today}
              /* Deliberately uncapped: a range may be drawn across booked days,
                 and buildQuote then refuses to price it and says which days
                 are taken. */
              filterDate={isDateSelectable}
              monthsShown={2}
              dateFormat="MMM d, yyyy"
              className={classes.input}
              onChange={(range) => {
                const [start, end] = range as [Date | null, Date | null];
                if (start) setRangeStart(start);
                setRangeEnd(end);
              }}
            />
            {selectedDays <= 1 && (
              <p className={classes.hint}>
                Need the venue for more than one day? Pick a start and end date
                — additional days are discounted.
              </p>
            )}
          </div>

          {result.status === "error" ? (
            <p className={classes.error}>{result.message}</p>
          ) : (
            quote && (
              <>
                <p className={classes.stayLine}>
                  Check in <strong>{formatLongDate(rangeStart)} at 2:00 PM</strong>,
                  check out{" "}
                  <strong>
                    {formatLongDate(checkOutDateFor(effectiveEnd))} at 10:00 AM
                  </strong>{" "}
                  · {nightCount} {nightCount === 1 ? "night" : "nights"}
                </p>

                <table className={classes.breakdown}>
                  <tbody>
                    {quote.lines.map((line) => (
                      <tr key={line.date.toISOString()}>
                        <th scope="row">
                          <span className={classes.lineDate}>
                            {line.date.toLocaleDateString("en-US", {
                              weekday: "short",
                              month: "short",
                              day: "numeric",
                            })}
                          </span>
                          <span className={classes.lineDesc}>
                            {line.description}
                          </span>
                        </th>
                        <td>{formatMoney(line.amount)}</td>
                      </tr>
                    ))}
                    <tr className={classes.subtotal}>
                      <th scope="row">Venue subtotal</th>
                      <td>{formatMoney(quote.venueSubtotal)}</td>
                    </tr>
                    <tr>
                      <th scope="row">Washington state sales tax (9.9%)</th>
                      <td>{formatMoney(quote.tax)}</td>
                    </tr>
                    <tr>
                      <th scope="row">
                        Security deposit
                        <span className={classes.lineDesc}>
                          Refunded after your event
                        </span>
                      </th>
                      <td>{formatMoney(quote.deposit)}</td>
                    </tr>
                    <tr className={classes.total}>
                      <th scope="row">Total</th>
                      <td>{formatMoney(quote.total)}</td>
                    </tr>
                  </tbody>
                </table>

                <div className={classes.schedule}>
                  <h3>Payment schedule</h3>
                  <p>
                    <strong>{formatMoney(quote.dueAtBooking)}</strong> due at
                    booking, and the remaining{" "}
                    <strong>{formatMoney(quote.dueOneMonthBefore)}</strong> due
                    one month before your event date.
                  </p>
                </div>

                <a className={classes.cta} href={buildBookingMailto(
                  packageName,
                  stayEventDate,
                  rangeStart,
                  effectiveEnd,
                  quote,
                )}>
                  Book this date
                </a>

                <details className={classes.fallback}>
                  <summary>Button didn't open your email app?</summary>
                  <p>
                    Some browsers and devices have no email app set up. Send us
                    a message directly at{" "}
                    <a href={`mailto:${BOOKING_EMAIL}`}>{BOOKING_EMAIL}</a> and
                    include the details below.
                  </p>
                  <pre className={classes.emailText}>{emailText}</pre>
                  <button
                    type="button"
                    className={classes.copy}
                    onClick={copyEmail}
                  >
                    {copied ? "Copied" : "Copy these details"}
                  </button>
                </details>

                <p className={classes.disclaimer}>
                  This quote covers the venue only. Catering, florals, DJ and
                  sound, bartending and decor rentals are arranged separately.
                  Event insurance is required and typically runs $100–$400.
                </p>
              </>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default QuoteModal;
